import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface IDataTitleProps {
    label: JSX.Element | string;
    title: JSX.Element | string;
}

const TOOLTIP_DELAY = 400;
const SITE_PADDING = 40;
const CLOSE_DELAY = 100;
const TITLE_PADDING = 10;

const DataTitle: React.FC<IDataTitleProps> = ({ label, title }) => {
    const CLASS_NAME = 'lxs-data-title';
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<HTMLSpanElement>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hoverState = useRef({ wrapper: false, tooltip: false });

    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});

    const positionTooltip = () => {
        if (!wrapperRef.current || !tooltipRef.current) {
            return;
        }
        const triggerRect = wrapperRef.current.getBoundingClientRect();
        const tipRect = tooltipRef.current.getBoundingClientRect();

        const left = triggerRect.left + triggerRect.width / 2;
        const top = triggerRect.top - tipRect.height - TITLE_PADDING;
        const transform = 'translateX(-50%)';
        const style: React.CSSProperties = {
            position: 'fixed',
            left,
            top,
            transform,
            visibility: 'visible',
            height: tipRect.height,
            opacity: 1,
            zIndex: 9999
        };

        if (left + tipRect.width / 2 > window.innerWidth - SITE_PADDING) {
            style.left = window.innerWidth - SITE_PADDING - tipRect.width / 2;
        }
        if (left - tipRect.width / 2 < SITE_PADDING) {
            style.left = SITE_PADDING + tipRect.width / 2;
        }

        setTooltipStyle(style);
    };

    const handleWrapperEnter = () => {
        hoverState.current.wrapper = true;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            setTooltipVisible(true);
            requestAnimationFrame(positionTooltip);
        }, TOOLTIP_DELAY);
    };

    const handleWrapperLeave = () => {
        hoverState.current.wrapper = false;
        checkAndCloseTooltip();
    };

    const handleTooltipEnter = () => {
        hoverState.current.tooltip = true;
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
    };
    const handleTooltipLeave = () => {
        hoverState.current.tooltip = false;
        checkAndCloseTooltip();
    };

    const checkAndCloseTooltip = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }
        closeTimeoutRef.current = setTimeout(() => {
            if (!hoverState.current.wrapper && !hoverState.current.tooltip) {
                setTooltipVisible(false);
                setTooltipStyle({});
            }
        }, CLOSE_DELAY);
    };

    useEffect(() => {
        if (!tooltipVisible) {
            hoverState.current.wrapper = false;
            hoverState.current.tooltip = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        }
    }, [tooltipVisible]);

    return (
        <>
            <span
                className={CLASS_NAME}
                ref={wrapperRef}
                onMouseEnter={handleWrapperEnter}
                onMouseLeave={handleWrapperLeave}
                style={{ position: 'relative' }}
            >
                {label}
            </span>
            {tooltipVisible &&
                createPortal(
                    <span
                        ref={tooltipRef}
                        className={`${CLASS_NAME}__text`}
                        style={{
                            ...tooltipStyle,
                            position: 'fixed',
                            visibility: tooltipVisible ? 'visible' : 'hidden',
                            opacity: tooltipVisible ? 1 : 0,
                            display: 'block'
                        }}
                        onMouseEnter={handleTooltipEnter}
                        onMouseLeave={handleTooltipLeave}
                    >
                        {title}
                    </span>,
                    document.body
                )}
        </>
    );
};

export default DataTitle;
